import type {
  OntologyConcept,
  OntologyRelationType,
  OntologyMetaType,
  OntologyStatus,
  CreateConceptRequest,
  UpdateConceptRequest,
  CreateRelationRequest,
  UpdateRelationRequest,
} from '@/types/ontology';

// Legacy types for backward compatibility
export type OntologyLayer = 'CORE' | 'CUSTOMER' | 'DRAFT' | 'UNKNOWN';

export type OntologyTerm = {
  id: string;
  name: string;
  layer: OntologyLayer;
  type: string;
};

export type DomainDefinition = {
  id: string;
  name: string;
  description: string;
};

export type IdentificationResult = {
  layer: OntologyLayer;
  term?: OntologyTerm;
};

// Map OntologyStatus to OntologyLayer for backward compatibility
function statusToLayer(status: OntologyStatus): OntologyLayer {
  switch (status) {
    case 'CORE':
      return 'CORE';
    case 'APPROVED':
      return 'CUSTOMER';
    default:
      return 'UNKNOWN';
  }
}

// API-based ontology manager
export const ontologyManager = {
  /**
   * Fetch all concepts for a domain.
   */
  async fetchConcepts(
    domainId: string,
    token?: string | null,
    filters?: { metaType?: OntologyMetaType; status?: OntologyStatus }
  ): Promise<OntologyConcept[]> {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const params = new URLSearchParams();
    if (filters?.metaType) params.append('metaType', filters.metaType);
    if (filters?.status) params.append('status', filters.status);
    const queryString = params.toString() ? `?${params.toString()}` : '';

    const response = await fetch(`/api/dotnet/ontology/${domainId}/concepts${queryString}`, {
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch concepts: HTTP ${response.status}`);
    }

    return await response.json();
  },

  /**
   * Fetch all relationship types for a domain.
   */
  async fetchRelations(domainId: string, token?: string | null): Promise<OntologyRelationType[]> {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`/api/dotnet/ontology/${domainId}/relations`, {
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch relations: HTTP ${response.status}`);
    }

    return await response.json();
  },

  /**
   * Create a new concept.
   */
  async createConcept(
    domainId: string,
    data: CreateConceptRequest,
    token?: string | null
  ): Promise<OntologyConcept> {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`/api/dotnet/ontology/${domainId}/concepts`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `Failed to create concept: HTTP ${response.status}`);
    }

    return await response.json();
  },

  /**
   * Update an existing concept.
   */
  async updateConcept(
    id: string,
    data: UpdateConceptRequest,
    token?: string | null
  ): Promise<OntologyConcept> {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`/api/dotnet/ontology/concepts/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `Failed to update concept: HTTP ${response.status}`);
    }

    return await response.json();
  },

  /**
   * Delete a concept.
   */
  async deleteConcept(id: string, token?: string | null): Promise<void> {
    const headers: HeadersInit = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`/api/dotnet/ontology/concepts/${id}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `Failed to delete concept: HTTP ${response.status}`);
    }
  },

  /**
   * Create a new relationship type.
   */
  async createRelation(
    domainId: string,
    data: CreateRelationRequest,
    token?: string | null
  ): Promise<OntologyRelationType> {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`/api/dotnet/ontology/${domainId}/relations`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `Failed to create relation: HTTP ${response.status}`);
    }

    return await response.json();
  },

  /**
   * Update an existing relationship type.
   */
  async updateRelation(
    id: string,
    data: UpdateRelationRequest,
    token?: string | null
  ): Promise<OntologyRelationType> {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`/api/dotnet/ontology/relations/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `Failed to update relation: HTTP ${response.status}`);
    }

    return await response.json();
  },

  /**
   * Delete a relationship type.
   */
  async deleteRelation(id: string, token?: string | null): Promise<void> {
    const headers: HeadersInit = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`/api/dotnet/ontology/relations/${id}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `Failed to delete relation: HTTP ${response.status}`);
    }
  },

  /**
   * Fetch the ontology schema for a domain.
   */
  async fetchSchema(domainId: string, token?: string | null): Promise<any> {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`/api/dotnet/ontology/${domainId}/schema`, {
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch schema: HTTP ${response.status}`);
    }

    return await response.json();
  },

  /**
   * Legacy method for backward compatibility.
   * Identify a word against fetched concepts.
   */
  identifyWord(word: string, concepts: OntologyConcept[]): IdentificationResult {
    const normalize = (value: string) => value.toLowerCase();
    const entry = concepts.find((concept) => normalize(concept.name) === normalize(word));
    
    if (!entry) {
      return { layer: 'UNKNOWN' };
    }
    
    return {
      layer: statusToLayer(entry.status),
      term: {
        id: entry.id,
        name: entry.name,
        layer: statusToLayer(entry.status),
        type: entry.metaType,
      },
    };
  },

  /**
   * Legacy method for backward compatibility.
   * Get concepts grouped by layer/status.
   */
  getTermsByLayer(concepts: OntologyConcept[], layer: OntologyLayer): OntologyTerm[] {
    return concepts
      .filter((concept) => statusToLayer(concept.status) === layer)
      .map((concept) => ({
        id: concept.id,
        name: concept.name,
        layer: statusToLayer(concept.status),
        type: concept.metaType,
      }));
  },
};
